import {
  type APIChannel,
  type APIForumTag,
  type APIMessage,
  can,
  compareSnowflakes,
  formatRelative,
  parseMarkdown,
  PermissionFlags,
  selectActiveThreads,
  snowflakeDate,
  toPlainText,
} from '@alternativamais/chat-core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../../componentes/Avatar';
import { avisarErro } from '../../../componentes/Avisos';
import {
  Botao,
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
} from '../../../componentes/Flutuante';
import { Icone } from '../../../componentes/Icone';
import { Markdown } from '../../../componentes/Markdown';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';
import { useAgora, useNome } from '../../../chat/ganchos';
import { rotaDoCanal } from '../Canal';
import { Cabecalho } from '../cabecalho/Cabecalho';
import { Carregando, Vazio } from '../componentes';
import { ehImagem } from '../mensagem/Conteudo';
import { EmojiVisual } from '../SeletorEmoji';
import { PainelThread } from '../threads/PainelThread';
import { noTamanho } from '../../../componentes/imagens';

/** Fórum que exige etiqueta no post (`REQUIRE_TAG`). */
const EXIGE_ETIQUETA = 1 << 4;
const POR_PAGINA = 25;

type Ordem = 'last_message_time' | 'creation_time';
const ORDENS: Record<Ordem, string> = {
  last_message_time: 'atividade recente',
  creation_time: 'data de criação',
};

/**
 * Fórum (C31): buscar ou criar post, ordenar, filtrar por etiqueta e a
 * lista de posts com a primeira mensagem. O post abre ao lado (C46) e o
 * "Novo post" abre o formulário por cima da lista (C32).
 */
export function Forum({
  forumId,
  postId,
}: {
  forumId: string;
  postId?: string;
}) {
  const cliente = useCliente();
  const navegar = useNavigate();
  const forum = useEstado((estado) => estado.channels[forumId]);
  const podePostar = useEstado((estado) =>
    can(estado, forumId, PermissionFlags.SEND_MESSAGES),
  );
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>(() =>
    forum?.default_sort_order === 1 ? 'creation_time' : 'last_message_time',
  );
  const [etiquetas, setEtiquetas] = useState<string[]>([]);
  const [criando, setCriando] = useState(false);
  const [menuOrdem, setMenuOrdem] = useState<Posicao | null>(null);
  const [pagina, setPagina] = useState<{
    threads: APIChannel[];
    primeiras: Record<string, APIMessage>;
    temMais: boolean;
  } | null>(null);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const pedido = useRef(0);

  const termo = busca.trim();
  const carregar = useCallback(
    (deslocamento: number) => {
      const meu = ++pedido.current;
      if (deslocamento) setCarregandoMais(true);
      cliente.api
        .searchThreads(forumId, {
          name: termo || undefined,
          sort_by: ordem,
          sort_order: 'desc',
          limit: POR_PAGINA,
          offset: deslocamento,
          tag: etiquetas,
        })
        .then((resultado) => {
          if (meu !== pedido.current) return;
          const loja = cliente.store.getState();
          for (const thread of resultado.threads) {
            if (!loja.channels[thread.id]) loja.upsertChannel(thread);
          }
          const primeiras = Object.fromEntries(
            resultado.first_messages.map((mensagem) => [mensagem.id, mensagem]),
          );
          setPagina((atual) => ({
            threads: deslocamento
              ? [...(atual?.threads ?? []), ...resultado.threads]
              : resultado.threads,
            primeiras: deslocamento
              ? { ...(atual?.primeiras ?? {}), ...primeiras }
              : primeiras,
            temMais: !!resultado.has_more,
          }));
        })
        .catch(avisarErro)
        .finally(() => meu === pedido.current && setCarregandoMais(false));
    },
    [cliente, forumId, ordem, etiquetas, termo],
  );

  useEffect(() => {
    const espera = window.setTimeout(() => carregar(0), termo ? 300 : 0);
    return () => window.clearTimeout(espera);
  }, [carregar, termo]);

  // Posts abertos da store (os novos chegam pelo gateway) + os da busca.
  const abertos = useDerivado(
    (estado) => selectActiveThreads(estado, forumId),
    (estado) => [estado.channels, estado.guildThreadIds, estado.joinedThreads],
  );
  const posts = useMemo(() => {
    if (!pagina) return null;
    const normal = (texto: string) =>
      texto
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
    const filtro = normal(termo);
    const vistos = new Set(pagina.threads.map((thread) => thread.id));
    const novos = abertos.filter(
      (thread) =>
        !vistos.has(thread.id) &&
        (!filtro || normal(thread.name ?? '').includes(filtro)) &&
        (!etiquetas.length ||
          etiquetas.some((id) => thread.applied_tags?.includes(id))),
    );
    const chave = (thread: APIChannel) =>
      ordem === 'creation_time'
        ? thread.id
        : (thread.last_message_id ?? thread.id);
    return [...novos, ...pagina.threads].sort((a, b) =>
      compareSnowflakes(chave(b), chave(a)),
    );
  }, [pagina, abertos, termo, etiquetas, ordem]);

  if (!forum) return null;
  const tags = forum.available_tags ?? [];
  const fecharPost = () => navegar(rotaDoCanal(forum));

  return (
    <div className="flex min-w-0 grow flex-col bg-chat">
      <Cabecalho
        canalId={forumId}
        lateralAberta={false}
        aoAlternarLateral={() => undefined}
        textoDaBusca=""
        aoBuscar={() => undefined}
      />
      <div className="relative flex min-h-0 grow">
        <main className="rolagem flex min-w-0 grow flex-col overflow-y-auto px-6 pt-3 pb-6">
          <div className="flex h-10 shrink-0 items-center gap-3">
            <label className="flex h-10 min-w-0 grow items-center gap-2.5 rounded-lg bg-campo px-3 text-texto-3 focus-within:outline focus-within:outline-destaque">
              <Icone nome="busca" tamanho={18} />
              <input
                value={busca}
                onChange={(evento) => setBusca(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === 'Enter' && podePostar && termo)
                    setCriando(true);
                }}
                placeholder="Buscar ou criar um post…"
                aria-label="Buscar ou criar um post"
                className="min-w-0 grow border-0 bg-transparent text-[15px] text-texto outline-none placeholder:text-texto-3"
              />
            </label>
            {podePostar && (
              <Botao onClick={() => setCriando(true)} className="shrink-0">
                <Icone nome="mais" tamanho={18} />
                Novo post
              </Botao>
            )}
          </div>
          <div className="mt-3.5 flex min-h-7 shrink-0 flex-wrap items-center gap-2.5">
            <button
              type="button"
              aria-haspopup="menu"
              onClick={(evento) => {
                const caixa = evento.currentTarget.getBoundingClientRect();
                setMenuOrdem(
                  menuOrdem ? null : { x: caixa.left, y: caixa.bottom + 4 },
                );
              }}
              className="flex h-7 items-center gap-1 rounded-md pr-2 pl-1 text-[13px] font-medium text-texto-2 hover:bg-hover"
            >
              Ordenar:{' '}
              <strong className="font-semibold text-texto">
                {ORDENS[ordem]}
              </strong>
              <Icone nome="chevron-baixo" tamanho={14} />
            </button>
            {tags.length > 0 && (
              <>
                <span className="h-5 w-px shrink-0 bg-borda" />
                <div
                  role="group"
                  aria-label="Filtrar por etiqueta"
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <ChipEtiqueta
                    rotulo="Todas"
                    marcada={!etiquetas.length}
                    aoClicar={() => setEtiquetas([])}
                  />
                  {tags.map((tag) => (
                    <ChipEtiqueta
                      key={tag.id}
                      tag={tag}
                      rotulo={tag.name}
                      marcada={etiquetas.includes(tag.id)}
                      aoClicar={() =>
                        setEtiquetas((atual) =>
                          atual.includes(tag.id)
                            ? atual.filter((id) => id !== tag.id)
                            : [...atual, tag.id],
                        )
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          <div
            role="feed"
            aria-label={`Posts em #${forum.name}`}
            aria-busy={!posts}
            className="mt-3.5 flex flex-col gap-3"
          >
            {posts === null ? (
              <Carregando texto="Carregando posts…" />
            ) : posts.length === 0 ? (
              <Vazio
                icone="forum"
                titulo={
                  termo || etiquetas.length
                    ? 'Nenhum post encontrado'
                    : 'Nenhum post ainda'
                }
                texto={
                  podePostar ? 'Crie o primeiro com "Novo post".' : undefined
                }
              />
            ) : (
              posts.map((thread) => (
                <CartaoPost
                  key={thread.id}
                  threadId={thread.id}
                  reserva={thread}
                  primeira={pagina?.primeiras[thread.id]}
                  tags={tags}
                  aberto={thread.id === postId}
                  aoAbrir={() =>
                    navegar(`${rotaDoCanal(forum)}/threads/${thread.id}`)
                  }
                />
              ))
            )}
            {pagina?.temMais && (
              <button
                type="button"
                disabled={carregandoMais}
                onClick={() => carregar(pagina.threads.length)}
                className="h-9 self-center rounded-lg bg-ativo px-4 text-sm font-semibold hover:brightness-110 disabled:opacity-60"
              >
                {carregandoMais ? 'Carregando…' : 'Mostrar mais posts'}
              </button>
            )}
          </div>
        </main>
        {criando && (
          <NovoPost
            forum={forum}
            tituloInicial={termo}
            aoFechar={() => setCriando(false)}
            aoCriar={(id) => {
              setCriando(false);
              setBusca('');
              navegar(`${rotaDoCanal(forum)}/threads/${id}`);
            }}
          />
        )}
        {postId && (
          <div className="pointer-events-none absolute inset-0 z-10 flex justify-end [&>aside]:pointer-events-auto">
            <PainelThread
              key={postId}
              threadId={postId}
              aoFechar={fecharPost}
              largura={560}
              textoDoCompositor="Responder no post"
              cabecalhoExtra={<ResumoPost threadId={postId} tags={tags} />}
            />
          </div>
        )}
      </div>
      {menuOrdem && (
        <Flutuante
          ancora={menuOrdem}
          aoFechar={() => setMenuOrdem(null)}
          rotulo="Ordenar posts"
          papel="menu"
        >
          <CaixaMenu largura={220}>
            {(Object.keys(ORDENS) as Ordem[]).map((valor) => (
              <ItemMenu
                key={valor}
                icone={valor === ordem ? 'check' : undefined}
                aoEscolher={() => {
                  setOrdem(valor);
                  setMenuOrdem(null);
                }}
              >
                {ORDENS[valor][0].toUpperCase() + ORDENS[valor].slice(1)}
              </ItemMenu>
            ))}
          </CaixaMenu>
        </Flutuante>
      )}
    </div>
  );
}

function ChipEtiqueta({
  rotulo,
  tag,
  marcada,
  aoClicar,
}: {
  rotulo: string;
  tag?: APIForumTag;
  marcada: boolean;
  aoClicar: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={marcada}
      onClick={aoClicar}
      className={`flex h-7 items-center gap-1 rounded-full border px-3 text-[13px] font-semibold whitespace-nowrap ${
        marcada
          ? 'border-destaque bg-destaque-suave text-destaque-claro'
          : 'border-borda text-texto-2 hover:border-texto-3'
      }`}
    >
      {tag && (tag.emoji_id || tag.emoji_name) && (
        <EmojiVisual
          emoji={{ id: tag.emoji_id, name: tag.emoji_name }}
          tamanho={14}
        />
      )}
      {rotulo}
    </button>
  );
}

export function EtiquetaPost({ tag }: { tag: APIForumTag }) {
  return (
    <span className="flex h-5 items-center gap-1 rounded bg-ativo px-1.5 text-[11px] font-bold tracking-[.02em] whitespace-nowrap text-texto-2">
      {(tag.emoji_id || tag.emoji_name) && (
        <EmojiVisual
          emoji={{ id: tag.emoji_id, name: tag.emoji_name }}
          tamanho={12}
        />
      )}
      {tag.name}
    </span>
  );
}

function CartaoPost({
  threadId,
  reserva,
  primeira,
  tags,
  aberto,
  aoAbrir,
}: {
  threadId: string;
  reserva: APIChannel;
  primeira?: APIMessage;
  tags: APIForumTag[];
  aberto: boolean;
  aoAbrir: () => void;
}) {
  const cliente = useCliente();
  const thread = useEstado((estado) => estado.channels[threadId]) ?? reserva;
  const daLoja = useEstado(
    (estado) => estado.messages[threadId]?.byId[threadId],
  );
  const [buscada, setBuscada] = useState<APIMessage | null>(null);
  const mensagem = daLoja ?? primeira ?? buscada;
  const agora = useAgora(60_000);
  const autorId = mensagem?.author.id ?? thread.owner_id ?? '';
  const autor = useNome(autorId, thread.guild_id, mensagem?.author);
  const nomes = useEstado((estado) => estado.users);
  const canais = useEstado((estado) => estado.channels);

  // Post novo (chegou pelo gateway): a primeira mensagem vem à parte.
  useEffect(() => {
    if (daLoja || primeira) return;
    let vivo = true;
    cliente.api
      .message(threadId, threadId)
      .then((achada) => vivo && setBuscada(achada))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cliente, daLoja, primeira, threadId]);

  const previa = useMemo(() => {
    if (!mensagem?.content) return '';
    return toPlainText(parseMarkdown(mensagem.content), {
      user: (id) =>
        nomes[id]?.global_name ?? nomes[id]?.username ?? 'desconhecido',
      role: () => 'cargo',
      channel: (id) => canais[id]?.name ?? 'canal',
    });
  }, [mensagem, nomes, canais]);
  const imagem = mensagem?.attachments.find((anexo) => ehImagem(anexo));
  const aplicadas = (thread.applied_tags ?? [])
    .map((id) => tags.find((tag) => tag.id === id))
    .filter((tag): tag is APIForumTag => !!tag);
  const total = thread.message_count ?? 0;
  const ultima = thread.last_message_id ?? thread.id;
  const arquivado = !!thread.thread_metadata?.archived;

  return (
    <article
      aria-label={thread.name ?? undefined}
      aria-current={aberto ? 'true' : undefined}
      className={`relative flex cursor-pointer items-center gap-4 rounded-[10px] p-4 ${
        aberto ? 'bg-ativo' : 'bg-elevado hover:bg-hover'
      }`}
      onClick={aoAbrir}
    >
      <div className="flex min-w-0 grow flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="m-0 text-base leading-[22px] font-semibold">
            <button
              type="button"
              onClick={(evento) => {
                evento.stopPropagation();
                aoAbrir();
              }}
              className="text-left after:absolute after:inset-0"
            >
              {thread.name}
            </button>
          </h2>
          {aplicadas.map((tag) => (
            <EtiquetaPost key={tag.id} tag={tag} />
          ))}
          {arquivado && (
            <span className="flex h-5 items-center gap-1 rounded bg-chat px-1.5 text-[11px] font-bold text-texto-3">
              <Icone nome="arquivo" tamanho={12} />
              Arquivado
            </span>
          )}
        </div>
        {previa && (
          <p className="m-0 line-clamp-2 text-sm leading-5 text-texto-2">
            {previa}
          </p>
        )}
        <div className="mt-1 flex min-w-0 items-center gap-2 text-[13px] text-texto-3">
          {mensagem && (
            <Avatar
              pessoa={mensagem.author}
              nome={autor}
              tamanho={20}
              fundo="var(--cor-elevado)"
            />
          )}
          <span className="truncate font-semibold text-texto-2">{autor}</span>
          <span aria-hidden="true" className="text-apagado">
            ·
          </span>
          <span className="flex items-center gap-1 whitespace-nowrap">
            <Icone nome="thread" tamanho={16} />
            {total} {total === 1 ? 'mensagem' : 'mensagens'}
          </span>
          <span aria-hidden="true" className="text-apagado">
            ·
          </span>
          <span className="whitespace-nowrap">
            atividade{' '}
            {formatRelative(
              // O relógio da tela anda de minuto em minuto: nada "no futuro".
              Math.min(snowflakeDate(ultima).getTime(), agora),
              new Date(agora),
            )}
          </span>
        </div>
      </div>
      {imagem && (
        <img
          src={noTamanho(imagem.proxy_url || imagem.url, 96)}
          alt={imagem.description ?? imagem.filename}
          loading="lazy"
          className="h-[72px] w-24 shrink-0 rounded-lg border border-borda object-cover"
        />
      )}
    </article>
  );
}

/** Etiquetas, mensagens, data e "Seguindo" embaixo do título do post (C46). */
function ResumoPost({
  threadId,
  tags,
}: {
  threadId: string;
  tags: APIForumTag[];
}) {
  const cliente = useCliente();
  const thread = useEstado((estado) => estado.channels[threadId]);
  const seguindo = useEstado((estado) => !!estado.joinedThreads[threadId]);
  if (!thread) return null;
  const aplicadas = (thread.applied_tags ?? [])
    .map((id) => tags.find((tag) => tag.id === id))
    .filter((tag): tag is APIForumTag => !!tag);
  const total = thread.message_count ?? 0;
  return (
    <>
      {aplicadas.map((tag) => (
        <EtiquetaPost key={tag.id} tag={tag} />
      ))}
      <span className="min-w-0 truncate text-xs text-texto-3">
        {total} {total === 1 ? 'mensagem' : 'mensagens'} · criado em{' '}
        {snowflakeDate(thread.id).toLocaleDateString('pt-BR')}
      </span>
      <span className="grow" />
      <button
        type="button"
        aria-pressed={seguindo}
        onClick={() =>
          (seguindo
            ? cliente.api.leaveThread(threadId)
            : cliente.api.joinThread(threadId)
          ).catch(avisarErro)
        }
        className={`flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-semibold ${
          seguindo
            ? 'bg-destaque-suave text-destaque-claro'
            : 'bg-ativo text-texto hover:brightness-110'
        }`}
      >
        <Icone nome={seguindo ? 'check' : 'sino'} tamanho={14} />
        {seguindo ? 'Seguindo' : 'Seguir'}
      </button>
    </>
  );
}

/** Criar post (C32): título, etiquetas, conteúdo e anexos. */
function NovoPost({
  forum,
  tituloInicial,
  aoFechar,
  aoCriar,
}: {
  forum: APIChannel;
  tituloInicial: string;
  aoFechar: () => void;
  aoCriar: (id: string) => void;
}) {
  const cliente = useCliente();
  const moderador = useEstado((estado) =>
    can(estado, forum.id, PermissionFlags.MANAGE_THREADS),
  );
  const [titulo, setTitulo] = useState(tituloInicial);
  const [texto, setTexto] = useState('');
  const [escolhidas, setEscolhidas] = useState<string[]>([]);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [menuTags, setMenuTags] = useState<Posicao | null>(null);
  const [enviando, setEnviando] = useState(false);
  const entradaArquivo = useRef<HTMLInputElement>(null);
  const tags = (forum.available_tags ?? []).filter(
    (tag) => !tag.moderated || moderador,
  );
  const exige = ((forum.flags ?? 0) & EXIGE_ETIQUETA) !== 0 && tags.length > 0;
  const valido =
    !!titulo.trim() &&
    (!!texto.trim() || arquivos.length > 0) &&
    (!exige || escolhidas.length > 0);

  useEffect(() => {
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape' && !menuTags) aoFechar();
    };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [aoFechar, menuTags]);

  const postar = () => {
    if (!valido || enviando) return;
    setEnviando(true);
    cliente.api
      .startThread(
        forum.id,
        {
          name: titulo.trim().slice(0, 100),
          applied_tags: escolhidas,
          message: {
            content: texto.trim(),
            ...(arquivos.length
              ? {
                  attachments: arquivos.map((arquivo, indice) => ({
                    id: String(indice),
                    filename: arquivo.name,
                  })),
                }
              : {}),
          },
        },
        arquivos.map((arquivo) => ({ name: arquivo.name, data: arquivo })),
      )
      .then((criada) => {
        cliente.store.getState().upsertChannel(criada);
        aoCriar(criada.id);
      })
      .catch((erro) => {
        avisarErro(erro);
        setEnviando(false);
      });
  };

  return (
    <>
      <div
        aria-hidden="true"
        className="absolute inset-0 z-10 bg-[rgba(5,6,8,.4)]"
        onClick={aoFechar}
      />
      <form
        aria-label={`Criar post em #${forum.name}`}
        onSubmit={(evento) => {
          evento.preventDefault();
          postar();
        }}
        className="absolute top-1 right-6 left-6 z-20 flex flex-col gap-3 rounded-xl bg-elevado p-4 shadow-[0_12px_32px_rgba(0,0,0,.45)]"
      >
        <label className="sr-only" htmlFor="titulo-post">
          Título do post
        </label>
        <input
          id="titulo-post"
          autoFocus
          maxLength={100}
          value={titulo}
          onChange={(evento) => setTitulo(evento.target.value)}
          placeholder="Título do post"
          className="h-11 rounded-lg border border-transparent bg-campo px-3 text-base font-semibold text-texto outline-none placeholder:text-texto-3 focus:border-destaque"
        />
        {tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span
              id="rotulo-etiquetas"
              className="text-[13px] font-semibold text-texto-3"
            >
              Etiquetas{exige ? ' (obrigatória)' : ''}:
            </span>
            <ul
              aria-labelledby="rotulo-etiquetas"
              className="m-0 flex list-none flex-wrap gap-1.5 p-0"
            >
              {escolhidas.map((id) => {
                const tag = tags.find((item) => item.id === id);
                if (!tag) return null;
                return (
                  <li
                    key={id}
                    className="flex h-7 items-center gap-1 rounded-full bg-destaque-suave pr-1 pl-3 text-[13px] font-semibold text-destaque-claro"
                  >
                    {tag.name}
                    <button
                      type="button"
                      aria-label={`Remover etiqueta ${tag.name}`}
                      onClick={() =>
                        setEscolhidas((atual) =>
                          atual.filter((item) => item !== id),
                        )
                      }
                      className="flex size-5 items-center justify-center rounded-full hover:bg-black/20"
                    >
                      <Icone nome="x" tamanho={12} />
                    </button>
                  </li>
                );
              })}
            </ul>
            {escolhidas.length < 5 && (
              <button
                type="button"
                onClick={(evento) => {
                  const caixa = evento.currentTarget.getBoundingClientRect();
                  setMenuTags(
                    menuTags ? null : { x: caixa.left, y: caixa.bottom + 4 },
                  );
                }}
                className="flex h-7 items-center gap-1 rounded-full border border-dashed border-borda px-3 text-[13px] font-semibold text-texto-2 hover:border-texto-3"
              >
                <Icone nome="etiqueta" tamanho={14} />
                Etiqueta
              </button>
            )}
          </div>
        )}
        <label className="sr-only" htmlFor="texto-post">
          Conteúdo do post
        </label>
        <textarea
          id="texto-post"
          rows={6}
          maxLength={2000}
          value={texto}
          onChange={(evento) => setTexto(evento.target.value)}
          placeholder="Escreva o post. Dá para usar markdown."
          className="min-h-32 resize-y rounded-lg border border-transparent bg-campo p-3 text-[15px] leading-[1.45] text-texto outline-none placeholder:text-texto-3 focus:border-destaque"
        />
        {arquivos.length > 0 && (
          <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
            {arquivos.map((arquivo, indice) => (
              <li
                key={`${arquivo.name}-${indice}`}
                className="flex h-8 items-center gap-1.5 rounded-lg bg-chat pr-1 pl-2.5 text-[13px]"
              >
                <Icone nome="clipe" tamanho={14} className="text-texto-3" />
                <span className="max-w-48 truncate">{arquivo.name}</span>
                <button
                  type="button"
                  aria-label={`Tirar ${arquivo.name}`}
                  onClick={() =>
                    setArquivos((lista) =>
                      lista.filter((_, posicao) => posicao !== indice),
                    )
                  }
                  className="flex size-6 items-center justify-center rounded text-texto-3 hover:text-ocupado"
                >
                  <Icone nome="x" tamanho={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Anexar arquivo"
            title="Anexar arquivo"
            onClick={() => entradaArquivo.current?.click()}
            className="flex size-9 items-center justify-center rounded-lg text-texto-2 hover:bg-ativo hover:text-texto"
          >
            <Icone nome="clipe" tamanho={20} />
          </button>
          <input
            ref={entradaArquivo}
            type="file"
            multiple
            hidden
            onChange={(evento) => {
              setArquivos((lista) =>
                [...lista, ...(evento.target.files ?? [])].slice(0, 10),
              );
              evento.target.value = '';
            }}
          />
          <span className="grow text-[13px] text-texto-3">
            Cada post vira uma thread em #{forum.name}.
          </span>
          <Botao tipo="fantasma" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao type="submit" disabled={!valido || enviando}>
            Postar
          </Botao>
        </div>
        {forum.topic && (
          <details className="text-[13px] text-texto-3">
            <summary className="cursor-pointer font-semibold">
              Diretrizes do fórum
            </summary>
            <div className="mt-1">
              <Markdown texto={forum.topic} guildId={forum.guild_id ?? null} />
            </div>
          </details>
        )}
      </form>
      {menuTags && (
        <Flutuante
          ancora={menuTags}
          aoFechar={() => setMenuTags(null)}
          rotulo="Escolher etiqueta"
          papel="menu"
        >
          <CaixaMenu largura={220}>
            {tags.map((tag) => (
              <ItemMenu
                key={tag.id}
                icone={escolhidas.includes(tag.id) ? 'check' : undefined}
                aoEscolher={() => {
                  setEscolhidas((atual) =>
                    atual.includes(tag.id)
                      ? atual.filter((item) => item !== tag.id)
                      : [...atual, tag.id].slice(0, 5),
                  );
                  setMenuTags(null);
                }}
              >
                {tag.name}
              </ItemMenu>
            ))}
          </CaixaMenu>
        </Flutuante>
      )}
    </>
  );
}
